import { rememberAdminSession } from '@/lib/adminSession'
import { supabase } from '@/lib/supabase'

const AUTH_BASE = '/auth'

/** Publishable key for dgxnnwnugdxzeopleera — same as hub auth proxy. */
const AUTH_APIKEY = 'sb_publishable_Uvr3BAwELJRoqXHFs7x7kQ_u2HCxSgm'

export type PasswordVerifyResult = {
  ok: true
  login_attempt: string
  next: 'otp' | 'totp' | 'totp_enroll' | 'session'
  enrollment_prompt?: boolean
  effective_trust_days: number
}

export type OtpVerifyResult = {
  ok: true
  access_token?: string
  refresh_token?: string
  expires_in?: number
  user?: { id: string; email: string }
  error?: string
}

function mapAuthError(code: string): string {
  switch (code) {
    case 'invalid_code':
    case 'code_replayed':
      return 'Invalid authenticator code. Try again.'
    case 'code_expired':
    case 'code_burned':
      return 'Code expired. Sign in again.'
    case 'locked':
      return 'Too many attempts. Try again later.'
    case 'invalid_credentials':
    case 'password_required':
      return 'Invalid email or password.'
    case 'auth_upstream_unreachable':
    case 'auth_upstream_error':
      return 'Authentication service unavailable. Try again.'
    case 'totp_required':
      return 'Authenticator code required. Use the same app as JumpStart hub.'
    default:
      return 'Sign-in failed. Try again.'
  }
}

export function mapOtpAuthError(code: string): string {
  return mapAuthError(code)
}

export function formatOtpAuthError(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err)
  return mapAuthError(msg)
}

async function authFetch<T>(
  fn: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<{ data: T; response: Response }> {
  const { json, ...requestInit } = init
  const headers = new Headers(init.headers)
  headers.set('Content-Type', 'application/json')
  headers.set('apikey', AUTH_APIKEY)

  let response: Response
  try {
    response = await fetch(`${AUTH_BASE}/${fn}`, {
      ...requestInit,
      headers,
      credentials: 'include',
      body: json !== undefined ? JSON.stringify(json) : init.body,
    })
  } catch {
    throw new Error('auth_upstream_unreachable')
  }

  if (response.status >= 500) {
    throw new Error('auth_upstream_error')
  }

  const data = (await response.json().catch(() => ({}))) as T
  return { data, response }
}

export const otpAuthApi = {
  passwordVerify: async (email: string, password: string) => {
    const { data, response } = await authFetch<PasswordVerifyResult>('auth-password-verify', {
      method: 'POST',
      json: { email, password, tenant: 'jumpstart' },
    })
    if (!response.ok) {
      throw new Error((data as { error?: string }).error ?? 'invalid_credentials')
    }
    return data
  },

  /** Verify TOTP (or OTP) and set the assured Supabase session — same as hub. */
  verify: async (loginAttempt: string, code: string, remember = false) => {
    const { data, response } = await authFetch<OtpVerifyResult>('auth-otp-verify', {
      method: 'POST',
      // remember:false — admin must enter TOTP every sign-in; no device trust skip
      json: { login_attempt: loginAttempt, code, remember: false },
    })
    if (!response.ok) {
      throw new Error((data as { error?: string }).error ?? 'otp_verify_failed')
    }
    if (!data.access_token || !data.refresh_token) {
      throw new Error('session_issue_failed')
    }
    rememberAdminSession(remember)
    const { error } = await supabase.auth.setSession({
      access_token: data.access_token,
      refresh_token: data.refresh_token,
    })
    if (error) throw error
    return data
  },
}
